import { Button, Checkbox } from '@timelyapp/react';
import classNames from 'classnames';
import { Close } from 'components/Icons';
import React from 'react';
import type { Tag } from 'types/tag';
import styles from './TagListCard.module.scss';

// --- TagListCard ---

interface TagListCardProps {
  children: React.ReactNode;
  className?: string;
}

export const TagListCard: React.FC<TagListCardProps> = ({ children, className }) => (
  <div className={classNames(styles.card, className)}>{children}</div>
);

// --- TagListCardHeader ---

interface TagListCardHeaderProps {
  name: string;
  onRemove: () => void;
  checked?: boolean;
  indeterminate?: boolean;
  onChangeChecked?: (checked: boolean) => void;
  children?: React.ReactNode;
}

export const TagListCardHeader: React.FC<TagListCardHeaderProps> = ({
  name,
  onRemove,
  checked,
  indeterminate,
  onChangeChecked,
  children,
}) => (
  <div className={styles.header}>
    {checked !== undefined && (
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        onChangeChecked={onChangeChecked}
        aria-label={`Select/deselect all tags in ${name}`}
      />
    )}
    <span className={styles.headerName}>{name}</span>
    {children}
    <Button priority="low" size="small" onClick={onRemove} className={styles.removeButton}>
      <Close />
    </Button>
  </div>
);

// --- TagChildRow ---

interface TagChildRowProps {
  tag: Pick<Tag, 'id' | 'name' | 'emoji' | 'active'>;
  checked?: boolean;
  onToggle?: (id: number, checked: boolean) => void;
  prefix?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export const TagChildRow: React.FC<TagChildRowProps> = ({
  tag,
  checked,
  onToggle,
  prefix,
  children,
  className,
}) => (
  <div className={classNames(styles.row, className)}>
    {checked !== undefined && (
      <Checkbox
        checked={checked}
        onChangeChecked={val => onToggle?.(tag.id, val)}
        disabled={tag.active === false}
        aria-label={`Mark tag ${tag.name} as selected`}
      />
    )}
    {prefix}
    {tag.emoji && <img className={styles.emoji} src={tag.emoji} alt={tag.name} />}
    <span>{tag.name}</span>
    {children && <div className={styles.rowTrailing}>{children}</div>}
  </div>
);
